// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useOrganization from "@/hooks/useOrganization";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { Translation } from "@/translations/types";
import type { TyLang, TySubscription, TySubscriptionConfig, TySubscriptionPeriod, TySubscriptionStatus, TySubscriptionView } from "@/types";
import { copyText, formatBytes, formatDate, formatMoney, readLangValue } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import StatusChip from "@/UI/atoms/StatusChip";
import ActionsBar from "@/UI/components/ActionsBar";
import EmptyState from "@/UI/components/EmptyState";
import HeaderPanel from "@/UI/components/HeaderPanel";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import PlanRequestPopup from "@/UI/popups/PlanRequestPopup";
import StorageRequestPopup from "@/UI/popups/StorageRequestPopup";
import "./styles.css";
// --- Helpers ------------------------------------------------------------------------------------
/**
 * A status is a derived value with a colour, and the chip set already has the five tones this
 * needs — so a subscription borrows them rather than inventing a sixth palette for six words.
 */
const STATUS_TONES: Record<TySubscriptionStatus, "available" | "reserved" | "sold" | "neutral" | "danger"> = {
    active: "available",
    grace: "reserved",
    pending: "reserved",
    coming: "sold",
    ended: "danger",
    cancelled: "neutral",
};

/** Whole days from now, never negative: a period that ended yesterday has none left, not minus one. */
const daysUntil = (value: string | null): number => {
    if (!value) return 0;
    return Math.max(0, Math.ceil((new Date(value).getTime() - Date.now()) / 86_400_000));
};
// --- Pieces -------------------------------------------------------------------------------------
type TyCommon = { t: Translation; lang: TyLang };

/**
 * One figure against its ceiling. The bar turns as it fills, because that is the only warning.
 *
 * **The two numbers are drawn differently on purpose.** What you have used is the figure somebody
 * came to read; what you are allowed is the context for it. Printed at the same weight they read
 * as one string — "17.3 MB of 5 GB" — and the eye has to parse a sentence to find the one number
 * that matters.
 */
function Meter({
    label,
    used,
    limit,
    usedText,
    totalText,
}: {
    label: string;
    used: number;
    limit: number | null;
    usedText: string;
    totalText: string;
}) {
    const percent = limit && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    const level = percent >= 100 ? "full" : percent >= 90 ? "high" : "fine";

    return (
        <div className="sub-meter" data-level={level}>
            <span className="sub-meter__label">{label}</span>

            <div className="sub-meter__figures">
                <span className="sub-meter__used">{usedText}</span>
                <span className="sub-meter__total">{totalText}</span>
            </div>

            {limit !== null && (
                <div className="sub-meter__bar">
                    <span className="sub-meter__fill" style={{ width: `${percent}%` }} />
                </div>
            )}
        </div>
    );
}

/** A value you have to retype into a banking app is a value with a copy button beside it. */
function CopyRow({ label, value, t }: { label: string; value: string; t: Translation }) {
    const [isCopied, setIsCopied] = useState(false);

    const handleCopy = async () => setIsCopied(await copyText(value));

    return (
        <div className="sub-copy">
            <span className="sub-copy__label">{label}</span>
            {/* An account number reads left to right in every language. */}
            <span className="sub-copy__value" dir="ltr">
                {value}
            </span>
            <button type="button" className="sub-copy__button" onClick={handleCopy}>
                <Icon name="copy" size={14} />
                <span>{isCopied ? t.common.copied : t.common.copy}</span>
            </button>
        </div>
    );
}

/** One thing waiting on a transfer: what it is for, and how much. */
export type TyOwed = { key: string; label: string; amount: number };

/**
 * How to pay, and the only checkout this product has. There is no provider wired and none should
 * be: a company transfers the amount and an admin confirms it, so the account details and a way to
 * send the receipt **are** the payment screen.
 *
 * **It takes a list, and appears once.** A company can owe two separate transfers at the same time
 * — a renewal and a storage pack — and printing the bank details twice would make somebody wonder
 * whether they are two different accounts. The account is shown once and each amount gets its own
 * WhatsApp button, because each is a receipt of its own.
 */
function HowToPay({ owed, config, t, organizationName }: TyCommon & { owed: TyOwed[]; config: TySubscriptionConfig; organizationName: string }) {
    /**
     * The message names the company and the amount: the person reading it has a bank statement and
     * a list of transfers, and nothing else to match them with.
     */
    const linkFor = (item: TyOwed) => {
        const lines = [t.subscription.title, organizationName, `${item.label} · ${formatMoney(item.amount)} ${t.properties.currency}`];
        return `https://wa.me/${config.billing.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(lines.join("\n"))}`;
    };

    return (
        <section className="sub-block">
            <h2 className="sub-block__title">{t.subscription.howToPay}</h2>

            <div className="sub-receivers">
                {config.billing.receivers.map((receiver) => (
                    <article key={receiver.iban} className="sub-receiver">
                        <div className="sub-receiver__head">
                            <span className="sub-receiver__bank">{receiver.bank}</span>
                            <span className="sub-receiver__name">{receiver.name}</span>
                        </div>
                        <CopyRow label={t.subscription.iban} value={receiver.iban} t={t} />
                    </article>
                ))}
            </div>

            {owed.map((item) => (
                <a key={item.key} className="sub-whatsapp" href={linkFor(item)} target="_blank" rel="noopener noreferrer">
                    <Icon name="phone" size={16} />
                    <span>
                        {t.subscription.sendReceipt}
                        {owed.length > 1 ? ` · ${item.label}` : ""}
                    </span>
                </a>
            ))}
        </section>
    );
}

/** The request itself: what is owed, and by when. The deadline is the loudest thing on it. */
function RequestCard({ request, t, lang, onWithdraw }: TyCommon & { request: TySubscription; onWithdraw: () => void }) {
    const left = daysUntil(request.paymentDueAt);

    return (
        <article className="sub-request">
            <StatusChip size="sm" tone={STATUS_TONES.pending} label={t.subscription.statuses.pending} />

            <div className="sub-request__amount">
                <span className="sub-request__number">{formatMoney(request.price)}</span>
                <span className="sub-request__currency">{t.properties.currency}</span>
            </div>
            <span className="sub-request__caption">{t.subscription.amountLabel}</span>

            <div className="sub-facts">
                <div className="sub-fact">
                    <span className="sub-fact__label">{t.subscription.periods[request.period]}</span>
                    <span className="sub-fact__value">{t.subscription.periodEvery[request.period]}</span>
                </div>
                <div className="sub-fact">
                    <span className="sub-fact__label">{t.subscription.payBy}</span>
                    <span className="sub-fact__value">
                        {formatDate(request.paymentDueAt, lang)} · {left} {t.subscription.daysLeft}
                    </span>
                </div>
                {request.requestedBy && (
                    <div className="sub-fact">
                        <span className="sub-fact__label">{t.subscription.requestedBy}</span>
                        <span className="sub-fact__value">{request.requestedBy.name}</span>
                    </div>
                )}
            </div>

            <button type="button" className="sub-request__withdraw" onClick={onWithdraw}>
                {t.subscription.withdraw}
            </button>
        </article>
    );
}

/** The live period. The number people come for is how long is left, so it is the only big one. */
function CurrentCard({
    current,
    config,
    t,
    lang,
    graceEndsAt,
}: TyCommon & { current: TySubscription; config: TySubscriptionConfig; graceEndsAt: string | null }) {
    const isGrace = current.status === "grace";
    const left = daysUntil(isGrace ? graceEndsAt : current.endsAt);
    const total = isGrace ? config.graceDays : config.months[current.period] * 30;
    const percent = total > 0 ? Math.min(100, Math.round((left / total) * 100)) : 0;
    const storageGb = current.storageGb + current.addons.reduce((sum, addon) => sum + addon.storageGb, 0);

    return (
        <article className={`sub-current${isGrace ? " sub-current--grace" : ""}`}>
            <div className="sub-current__top">
                <StatusChip size="sm" tone={STATUS_TONES[current.status]} label={t.subscription.statuses[current.status]} />
                <span className="sub-current__period">{t.subscription.periods[current.period]}</span>
            </div>

            <div className="sub-current__hero">
                <span className="sub-current__days">{left}</span>
                <span className="sub-current__days-label">{isGrace ? t.subscription.graceEnds : t.subscription.daysLeft}</span>
            </div>

            <div className="sub-current__bar">
                <span className="sub-current__fill" style={{ width: `${percent}%` }} />
            </div>

            <div className="sub-facts">
                <div className="sub-fact">
                    <span className="sub-fact__label">{t.subscription.startDate}</span>
                    <span className="sub-fact__value">{formatDate(current.startsAt, lang)}</span>
                </div>
                <div className="sub-fact">
                    <span className="sub-fact__label">{isGrace ? t.subscription.graceEnds : t.subscription.endDate}</span>
                    <span className="sub-fact__value">{formatDate(isGrace ? graceEndsAt : current.endsAt, lang)}</span>
                </div>
                <div className="sub-fact">
                    <span className="sub-fact__label">{t.subscription.storage}</span>
                    <span className="sub-fact__value">
                        {storageGb} {t.subscription.gb}
                    </span>
                </div>
                <div className="sub-fact">
                    <span className="sub-fact__label">{t.subscription.total}</span>
                    <span className="sub-fact__value">
                        {formatMoney(current.price)} {t.properties.currency}
                    </span>
                </div>
            </div>
        </article>
    );
}

/**
 * The period already paid for and waiting its turn. Dashed and quieter than the live one, because
 * the difference between "running" and "not started" is the only thing anybody reads these two for.
 */
function ComingCard({ coming, startsAt, t, lang }: TyCommon & { coming: TySubscription; startsAt: string | null }) {
    return (
        <article className="sub-coming">
            <p className="sub-coming__note">{t.subscription.comingNote}</p>
            <div className="sub-facts">
                <div className="sub-fact">
                    <span className="sub-fact__label">{t.subscription.periods[coming.period]}</span>
                    <span className="sub-fact__value">{t.subscription.periodEvery[coming.period]}</span>
                </div>
                <div className="sub-fact">
                    <span className="sub-fact__label">{t.subscription.comingStarts}</span>
                    <span className="sub-fact__value">{formatDate(startsAt, lang)}</span>
                </div>
                <div className="sub-fact">
                    <span className="sub-fact__label">{t.subscription.total}</span>
                    <span className="sub-fact__value">
                        {formatMoney(coming.price)} {t.properties.currency}
                    </span>
                </div>
            </div>
        </article>
    );
}

/**
 * Storage asked for and not transferred for yet. The same card as a period's request and for the
 * same reason — it is the same money arriving the same way — so it says what is owed, by when, and
 * offers the one way out.
 */
function StorageRequestCard({
    packs,
    gb,
    amount,
    dueAt,
    t,
    lang,
    onWithdraw,
}: TyCommon & { packs: number; gb: number; amount: number; dueAt: string | null; onWithdraw: () => void }) {
    return (
        <article className="sub-request">
            <StatusChip size="sm" tone={STATUS_TONES.pending} label={t.subscription.statuses.pending} />

            <div className="sub-request__amount">
                <span className="sub-request__number">{formatMoney(amount)}</span>
                <span className="sub-request__currency">{t.properties.currency}</span>
            </div>
            <span className="sub-request__caption">{t.subscription.amountLabel}</span>

            <div className="sub-facts">
                <div className="sub-fact">
                    <span className="sub-fact__label">{t.subscription.addedStorage}</span>
                    <span className="sub-fact__value">
                        {gb} {t.subscription.gb} · {packs} {t.subscription.packs}
                    </span>
                </div>
                <div className="sub-fact">
                    <span className="sub-fact__label">{t.subscription.payBy}</span>
                    <span className="sub-fact__value">
                        {formatDate(dueAt, lang)} · {daysUntil(dueAt)} {t.subscription.daysLeft}
                    </span>
                </div>
            </div>

            <button type="button" className="sub-request__withdraw" onClick={onWithdraw}>
                {t.subscription.withdrawStorage}
            </button>
        </article>
    );
}

/** The two plans, for a company that has nothing live. The screen has to sell before it reports. */
function PlanCards({ config, t, onPick }: TyCommon & { config: TySubscriptionConfig; onPick: (period: TySubscriptionPeriod) => void }) {
    return (
        <div className="sub-plans">
            {config.periods.map((period) => (
                <button key={period} type="button" className="sub-plan" onClick={() => onPick(period)}>
                    <span className="sub-plan__name">{t.subscription.periods[period]}</span>
                    <span className="sub-plan__price">
                        {formatMoney(config.prices[period])}
                        <small>{t.properties.currency}</small>
                    </span>
                    <span className="sub-plan__every">{t.subscription.periodEvery[period]}</span>
                    <span className="sub-plan__storage">{t.subscription.storageIncluded.replace("{gb}", String(config.planStorageGb))}</span>
                    <span className="sub-plan__cta">{t.subscription.request}</span>
                </button>
            ))}
        </div>
    );
}

/** One past period, as a row. History is read in a sweep, so it is rows rather than cards. */
function HistoryRow({ row, t, lang }: TyCommon & { row: TySubscription }) {
    const when = row.paidAt
        ? `${t.subscription.paidOn} ${formatDate(row.paidAt, lang)}`
        : `${t.subscription.requestedOn} ${formatDate(row.createdAt, lang)}`;

    return (
        <article className="sub-history-row">
            <StatusChip size="sm" tone={STATUS_TONES[row.status]} label={t.subscription.statuses[row.status]} />
            <div className="sub-history-row__text">
                <span className="sub-history-row__period">{t.subscription.periods[row.period]}</span>
                <span className="sub-history-row__when">
                    {when}
                    {row.startsAt && row.endsAt ? ` · ${formatDate(row.startsAt, lang)} — ${formatDate(row.endsAt, lang)}` : ""}
                </span>
            </div>
            <span className="sub-history-row__price">
                {formatMoney(row.price)} {t.properties.currency}
            </span>
        </article>
    );
}
// --- Logic --------------------------------------------------------------------------------------
/**
 * What this company pays us, and the one screen it can do anything about it from.
 *
 * **It is read in three completely different states** and each one is a different page: nothing
 * yet, a request waiting for its transfer, and a live period. They are not variations of one
 * layout — a company with no plan needs to be sold to, a company that has asked needs an account
 * number, and a company that is paying needs to know when it runs out — so the screen branches
 * rather than hiding half of itself behind empty values.
 *
 * `subscription` is in `CLIENT_SUPER_MODELS`, so a member never arrives here at all; the route is
 * gated and the nav entry is absent, not disabled.
 *
 * Nothing here ever grants access. The request is a request, the money is transferred by hand, and
 * only a Lozicore admin confirming it starts a period — every piece of copy on the screen says so,
 * because the one thing worse than waiting is thinking you are not.
 */
export default function SubscriptionScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const addComp = usePopup((state) => state.addComp);
    const organizationId = useOrganization((state) => state.organizationId);
    const memberships = useAuth((state) => state.memberships);
    const [params, setParams] = useSearchParams();

    const asked = params.get("tab") ?? "";
    const tab = asked === "history" || asked === "usage" ? asked : "overview";

    const view = useFetch<TySubscriptionView>("/subscription");
    const config = useFetch<{ config: TySubscriptionConfig }>("/subscription/config");
    const action = useFetch("/subscription", {}, false);

    useEffect(() => {
        if (!action.succeededAt) return;
        view.invoke();
    }, [action.succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    /** The receipt message names the company, so a transfer can be matched to one. */
    const active = memberships.find((item) => item.organization._id === organizationId) ?? null;
    const organizationName = active ? readLangValue(active.organization.name, lang) : "";

    const plan = config.data?.config ?? null;
    const data = view.data ?? null;

    /** The tab **replaces** rather than pushes: moving between two views is not a step to go back. */
    const goTab = (key: string) => {
        const merged = new URLSearchParams(params);
        if (key === "overview") merged.delete("tab");
        else merged.set("tab", key);
        setParams(merged, { replace: true });
    };

    const openRequest = (period?: TySubscriptionPeriod) => {
        if (!plan) return;
        addComp({
            name: "plan-request",
            Component: PlanRequestPopup,
            anim: "scale",
            details: { config: plan, period: period ?? data?.current?.period, isRenewal: !!data?.current, onDone: () => view.invoke() },
        });
    };

    const openStorage = () => {
        if (!plan || !data?.current) return;
        addComp({
            name: "storage-request",
            Component: StorageRequestPopup,
            anim: "scale",
            details: {
                config: plan,
                // A pack costs what the live period costs, so the form is told which that is.
                period: data.current.period,
                currentAllowed: data.usage.storageAllowed,
                onDone: () => view.invoke(),
            },
        });
    };

    const withdraw = (request: TySubscription) =>
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: t.subscription.withdraw,
                message: t.subscription.withdrawConfirm,
                confirmLabel: t.subscription.withdraw,
                onConfirm: () => action.invoke({ method: "PATCH", path: `/subscription/${request._id}/cancel` }),
            },
        });

    const withdrawStorage = () =>
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: t.subscription.withdrawStorage,
                message: t.subscription.withdrawStorageConfirm,
                confirmLabel: t.subscription.withdrawStorage,
                onConfirm: () => action.invoke({ method: "PATCH", path: "/subscription/storage/cancel" }),
            },
        });

    if (view.loading || config.loading) return <p className="sub-screen__state">{t.common.loading}</p>;
    if (!data || !plan) return <p className="sub-screen__state sub-screen__state--error">{view.error ?? config.error ?? t.common.somethingWrong}</p>;

    const { current, pending, coming, history, usage, limits } = data;
    /**
     * What sits under the used figure. The three states say three different things: while **free**
     * there is a ceiling to count against; on a **paid** plan there is none but storage; and a
     * **lapsed** one can create nothing at all, so calling it unlimited would promise headroom it
     * has not got — it gets nothing rather than a number that is not true.
     */
    const ceilingText = (limit?: number) =>
        limit !== undefined ? t.subscription.ofLimit.replace("{limit}", String(limit)) : data.access === "full" ? t.subscription.unlimited : "";

    /**
     * Packs asked for and not paid for yet, summed. They are written one row per pack in a single
     * breath and priced as one transfer, so the screen talks about them as one request.
     */
    const waiting = (current?.addons ?? []).filter((addon) => addon.status === "pending");
    const storageRequest = waiting.length
        ? {
              packs: waiting.length,
              gb: waiting.reduce((total, addon) => total + addon.storageGb, 0),
              amount: waiting.reduce((total, addon) => total + addon.amount, 0),
              dueAt: waiting[0].paymentDueAt,
          }
        : null;

    /** Everything waiting on a transfer, so the account details are printed once for all of it. */
    const owed: TyOwed[] = [
        ...(pending ? [{ key: pending._id, label: t.subscription.periods[pending.period], amount: pending.price }] : []),
        ...(storageRequest ? [{ key: "storage", label: t.subscription.buyStorageTitle, amount: storageRequest.amount }] : []),
    ];

    const common = { t, lang };
    /** A renewal while one is live; a first ask otherwise. The bar says which. */
    const addLabel = current
        ? current.status === "grace"
            ? t.subscription.renewNow
            : t.subscription.renew
        : data.access === "readOnly"
          ? t.subscription.reactivate
          : t.subscription.request;

    return (
        <section className="sub-screen">
            <HeaderPanel
                head={
                    <header className="sub-screen__head">
                        <div className="sub-screen__head-text">
                            <h1 className="sub-screen__title">{t.subscription.title}</h1>
                            <p className="sub-screen__hint">
                                {data.access === "free" ? t.subscription.freeNote : current ? t.subscription.usageHint : t.subscription.endedNote}
                            </p>
                        </div>
                        {current && <StatusChip tone={STATUS_TONES[current.status]} label={t.subscription.statuses[current.status]} />}
                    </header>
                }
            >
                <ActionsBar
                    tabs={[
                        { key: "overview", label: t.subscription.title },
                        { key: "usage", label: t.subscription.usageTab },
                        { key: "history", label: t.subscription.historyTitle },
                    ]}
                    activeTab={tab}
                    onTab={goTab}
                    /* Hidden while a request is open: the API refuses a second one, and a button
                       that always refuses is worse than no button. */
                    addLabel={pending ? undefined : addLabel}
                    onAdd={pending ? undefined : () => openRequest()}
                />
            </HeaderPanel>

            {action.error && <p className="sub-screen__state sub-screen__state--error">{action.error}</p>}

            {/* --- What they are using against what they are allowed ----------------------- */}
            {tab === "usage" && (
                <section className="sub-block">
                    <h2 className="sub-block__title">{t.subscription.usageTitle}</h2>
                    <p className="sub-block__note">{t.subscription.usageHint}</p>

                    <div className="sub-meters">
                        <Meter
                            label={t.subscription.storage}
                            used={usage.storageUsed}
                            limit={usage.storageAllowed}
                            usedText={formatBytes(usage.storageUsed, lang)}
                            totalText={t.subscription.ofLimit.replace("{limit}", formatBytes(usage.storageAllowed, lang))}
                        />

                        {/* The other three are only limits while free — a paid plan is unlimited
                            in everything but storage, so showing a bar would invent a ceiling. A
                            lapsed one is neither: it cannot create anything at all, so the count
                            stands on its own rather than promising headroom it has not got. */}
                        <Meter
                            label={t.subscription.projects}
                            used={usage.projects}
                            limit={limits?.projects ?? null}
                            usedText={String(usage.projects)}
                            totalText={ceilingText(limits?.projects)}
                        />
                        <Meter
                            label={t.subscription.units}
                            used={usage.units}
                            limit={limits?.units ?? null}
                            usedText={String(usage.units)}
                            totalText={ceilingText(limits?.units)}
                        />
                        <Meter
                            label={t.subscription.team}
                            used={usage.clients}
                            limit={limits?.clients ?? null}
                            usedText={String(usage.clients)}
                            totalText={ceilingText(limits?.clients)}
                        />
                    </div>

                    {/* Asked for here, where the meter that sent them looking is. */}
                    {current && !storageRequest && (
                        <div className="sub-more-storage">
                            <div>
                                <p className="sub-more-storage__title">{t.subscription.needMoreStorage}</p>
                                <p className="sub-more-storage__note">{t.subscription.needMoreStorageNote}</p>
                            </div>
                            <button type="button" className="sub-more-storage__link" onClick={openStorage}>
                                {t.subscription.buyStorage}
                            </button>
                        </div>
                    )}
                </section>
            )}

            {/* --- Past periods ------------------------------------------------------------ */}
            {tab === "history" && (
                <section className="sub-block">
                    {!history.length && <EmptyState icon="history" title={t.subscription.historyEmpty} />}
                    <div className="sub-history">
                        {history.map((row) => (
                            <HistoryRow key={row._id} row={row} {...common} />
                        ))}
                    </div>
                </section>
            )}

            {tab === "overview" && (
                <>
                    {/* --- What is wrong, if anything. Loud, and above everything else. --------- */}
                    {current?.status === "grace" && (
                        <div className="sub-alert sub-alert--warn">
                            <Icon name="history" size={18} />
                            <div>
                                <p className="sub-alert__title">{t.subscription.graceTitle}</p>
                                <p className="sub-alert__note">{t.subscription.graceNote}</p>
                            </div>
                        </div>
                    )}

                    {data.access === "readOnly" && (
                        <div className="sub-alert sub-alert--bad">
                            <Icon name="clear" size={18} />
                            <div>
                                <p className="sub-alert__title">{t.subscription.endedTitle}</p>
                                <p className="sub-alert__note">{t.subscription.endedNote}</p>
                            </div>
                        </div>
                    )}

                    {data.access === "free" && !pending && (
                        <div className="sub-alert">
                            <Icon name="subscription" size={18} />
                            <div>
                                <p className="sub-alert__title">{t.subscription.freeTitle}</p>
                                <p className="sub-alert__note">{t.subscription.freeNote}</p>
                            </div>
                        </div>
                    )}

                    {/**
                     * The periods, labelled and in order. A queued renewal used to sit under the
                     * live one as another card, and two cards in a column are two of the same
                     * thing — which is exactly what these are not: one is running and one has not
                     * started. The labels and the arrow between them say which is which before
                     * anybody reads a date.
                     */}
                    {current && (
                        <div className="sub-periods">
                            <section className="sub-period">
                                <span className="sub-period__label">{t.subscription.currentPeriod}</span>
                                <CurrentCard current={current} config={plan} graceEndsAt={data.graceEndsAt} {...common} />
                            </section>

                            {coming && (
                                <>
                                    <span className="sub-periods__link" aria-hidden="true">
                                        <Icon name="chevron" size={14} />
                                    </span>

                                    <section className="sub-period sub-period--next">
                                        <span className="sub-period__label">{t.subscription.nextPeriod}</span>
                                        <ComingCard coming={coming} startsAt={coming.startsAt ?? current.endsAt} {...common} />
                                    </section>
                                </>
                            )}
                        </div>
                    )}

                    {/* --- A storage pack asked for and not transferred for yet ---------------- */}
                    {storageRequest && (
                        <>
                            <div className="sub-alert sub-alert--warn">
                                <Icon name="drive" size={18} />
                                <div>
                                    <p className="sub-alert__title">{t.subscription.storagePendingTitle}</p>
                                    <p className="sub-alert__note">{t.subscription.storagePendingNote}</p>
                                </div>
                            </div>

                            <StorageRequestCard
                                packs={storageRequest.packs}
                                gb={storageRequest.gb}
                                amount={storageRequest.amount}
                                dueAt={storageRequest.dueAt}
                                onWithdraw={withdrawStorage}
                                {...common}
                            />
                        </>
                    )}

                    {/* --- The request for a period, and how to settle it ---------------------- */}
                    {pending && (
                        <>
                            <div className="sub-alert">
                                <Icon name="check" size={18} />
                                <div>
                                    <p className="sub-alert__title">{current ? t.subscription.pendingRenewalTitle : t.subscription.pendingTitle}</p>
                                    <p className="sub-alert__note">{current ? t.subscription.pendingRenewalNote : t.subscription.pendingNote}</p>
                                </div>
                            </div>

                            <RequestCard request={pending} onWithdraw={() => withdraw(pending)} {...common} />
                        </>
                    )}

                    {/* Once, under whatever is waiting — one account, however many transfers. */}
                    {owed.length > 0 && <HowToPay owed={owed} config={plan} organizationName={organizationName} {...common} />}

                    {/* --- Nothing live and nothing asked for: sell ---------------------------- */}
                    {!current && !pending && <PlanCards config={plan} onPick={(period) => openRequest(period)} {...common} />}
                </>
            )}
        </section>
    );
}
